import { Suspense, useEffect } from "react";
import { lazyWithReload } from "@/lib/lazy-with-reload";
import { Switch, Route, useLocation, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppLayout } from "@/layouts/AppLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageErrorBoundary } from "@/components/ui/page-error-boundary";
import { AdminGuard } from "@/components/AdminGuard";
import { SignupDialogProvider } from "@/contexts/SignupDialogContext";
import { CreditWallProvider } from "@/contexts/CreditWallContext";
import { AuthProvider } from "@/contexts/AuthContext";
import SignupDialog from "@/components/SignupDialog";
import ConsentBanner from "@/components/ConsentBanner";
import { AuthenticateWithRedirectCallback } from "@clerk/react";
import { fetchRouteRegistry } from "@/route-data";

// Fire-and-forget — populate route registry module cache before components need it
fetchRouteRegistry();

// Critical path — loaded eagerly
import Landing from "@/pages/landing";
import FunnelLanding from "@/pages/funnel-landing";
import NotFound from "@/pages/not-found";

// Lazy-loaded pages — split into separate chunks
const DashboardPage = lazyWithReload(() => import("@/pages/dashboard"));
const Discover = lazyWithReload(() => import("@/pages/discover"));
const ExplorePage = lazyWithReload(() => import("@/pages/explore"));
const TripDetailPage = lazyWithReload(() => import("@/pages/trip-detail"));
const CreateTrip = lazyWithReload(() => import("@/pages/create-trip"));
const ImportItineraryPage = lazyWithReload(() => import("@/pages/import-itinerary"));
const TravelDnaPage = lazyWithReload(() => import("@/pages/travel-dna"));
const PlanFromStars = lazyWithReload(() => import("@/pages/plan-from-stars"));
const MyTrips = lazyWithReload(() => import("@/pages/my-trips"));
const TripBoard = lazyWithReload(() => import("@/pages/trip-board"));
const Expenses = lazyWithReload(() => import("@/pages/expenses").then(m => ({ default: m.default })));
const TripExpensesPage = lazyWithReload(() => import("@/pages/expenses").then(m => ({ default: m.TripExpensesPage })));
const Packing = lazyWithReload(() => import("@/pages/packing"));
const FestivalGuidePage = lazyWithReload(() => import("@/pages/festival-guide"));
const RoutesPage = lazyWithReload(() => import("@/pages/routes"));
const RouteDetailPage = lazyWithReload(() => import("@/pages/route-detail"));
const ConferencePage = lazyWithReload(() => import("@/pages/conference/ConferencePage"));
const LocalEventPage = lazyWithReload(() => import("@/pages/local-event/LocalEventPage"));
const RouteSharePage = lazyWithReload(() => import("@/pages/route-share"));
const CreateRoutePage = lazyWithReload(() => import("@/pages/create-route"));
const RouteEditPage = lazyWithReload(() => import("@/pages/route-edit"));
const DailyGuidePage = lazyWithReload(() => import("@/pages/daily-guide"));
const NotificationsPage = lazyWithReload(() => import("@/pages/notifications"));
const Premium = lazyWithReload(() => import("@/pages/premium"));
const FriendsPage = lazyWithReload(() => import("@/pages/friends"));
const MessagesPage = lazyWithReload(() => import("@/pages/messages"));
const ReviewsPage = lazyWithReload(() => import("@/pages/reviews"));
const SettingsPage = lazyWithReload(() => import("@/pages/settings"));
const CommunityBoard = lazyWithReload(() => import("@/pages/community"));
const ProfilePage = lazyWithReload(() => import("@/pages/profile"));
const PointsMilesPage = lazyWithReload(() => import("@/pages/points-miles"));
const RewardsPage = lazyWithReload(() => import("@/pages/rewards"));
const FavoriteBoardPage = lazyWithReload(() => import("@/pages/favorite-board"));
const FavoritesLeaderboardPage = lazyWithReload(() => import("@/pages/favorites-leaderboard"));
const TravelCalendarPage = lazyWithReload(() => import("@/pages/travel-calendar"));
const ReferralsPage = lazyWithReload(() => import("@/pages/referrals"));
const TransactionsPage = lazyWithReload(() => import("@/pages/transactions"));
const AdminModeration = lazyWithReload(() => import("@/pages/admin-moderation"));
const AdminStrategy = lazyWithReload(() => import("@/pages/admin-strategy"));
const AdminQR = lazyWithReload(() => import("@/pages/admin-qr"));
const AdminPrivacy = lazyWithReload(() => import("@/pages/admin-privacy"));
const SignInPage = lazyWithReload(() => import("@/pages/sign-in"));
const SignUpPage = lazyWithReload(() => import("@/pages/sign-up"));
const TripPhotosPage = lazyWithReload(() => import("@/pages/trip-photos"));
const AllPhotosPage = lazyWithReload(() => import("@/pages/all-photos"));
const LocalEventsPage = lazyWithReload(() => import("@/pages/local-events"));
const SuitAndTiePage = lazyWithReload(() => import("@/pages/suit-and-tie"));
const CreateEntityPage = lazyWithReload(() => import("@/pages/create-entity"));
const CreaturePage = lazyWithReload(() => import("@/pages/creature/CreaturePage"));
const DevDesignSystem = import.meta.env.DEV
  ? lazyWithReload(() => import("@/pages/dev-design-system"))
  : null;

// Public pages — lazy since rarely visited
const PrivacyPolicy = lazyWithReload(() => import("@/pages/privacy-policy"));
const TermsOfService = lazyWithReload(() => import("@/pages/terms-of-service"));
const MessagingTerms = lazyWithReload(() => import("@/pages/messaging-terms"));
const AboutUs = lazyWithReload(() => import("@/pages/about"));
const HelpCenter = lazyWithReload(() => import("@/pages/help-center"));
const ContactUs = lazyWithReload(() => import("@/pages/contact"));
const CookiePolicyPage = lazyWithReload(() => import("@/pages/cookie-policy"));
const AccessibilityPage = lazyWithReload(() => import("@/pages/accessibility"));
const DoNotSellPage = lazyWithReload(() => import("@/pages/do-not-sell"));
const AiDisclosurePage = lazyWithReload(() => import("@/pages/ai-disclosure"));
const ThankYouPage = lazyWithReload(() => import("@/pages/thank-you"));
const MarketingUnsubscribePage = lazyWithReload(() => import("@/pages/MarketingUnsubscribePage"));
const SharedTripPage = lazyWithReload(() => import("@/pages/shared-trip"));
const FestivalJoinPage = lazyWithReload(() => import("@/pages/festival-join"));
const QrLandingPage = lazyWithReload(() => import("@/pages/qr-landing"));

function PageLoader() {
  return (
    <div role="status" aria-label="Loading" className="flex items-center justify-center min-h-[50vh]">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" aria-hidden="true" />
    </div>
  );
}

function AppRoutes() {
  const [location] = useLocation();
  return (
    <AppLayout>
      <ErrorBoundary resetKey={location}>
      <Suspense fallback={<PageLoader />}>
      <Switch>
        {/* IA Redesign: /explore is the cross-content front door (Phase 85) */}
        <Route path="/explore">{() => <PageErrorBoundary><ExplorePage /></PageErrorBoundary>}</Route>
        <Route path="/dashboard" component={DashboardPage} />
        <Route path="/discover" component={Discover} />
        <Route path="/swipe" component={Discover} />
        <Route path="/trip/:id/photos" component={TripPhotosPage} />
        <Route path="/trip/:id" component={TripDetailPage} />
        <Route path="/create-trip" component={CreateTrip} />
        <Route path="/import-itinerary" component={ImportItineraryPage} />
        <Route path="/travel-dna" component={TravelDnaPage} />
        <Route path="/plan-from-stars" component={PlanFromStars} />
        <Route path="/browse" component={FriendsPage} />
        <Route path="/my-trips">{() => <PageErrorBoundary><MyTrips /></PageErrorBoundary>}</Route>
        <Route path="/trip-board" component={TripBoard} />
        <Route path="/expenses/:id" component={TripExpensesPage} />
        <Route path="/expenses" component={Expenses} />
        <Route path="/packing" component={Packing} />
        <Route path="/festivals">{() => <Redirect to="/festival-guide" />}</Route>
        <Route path="/festival-guide" component={FestivalGuidePage} />
        {/* IA Redesign: /endeavor replaces /journeys */}
        <Route path="/endeavor" component={RoutesPage} />
        <Route path="/journeys">{() => <Redirect to="/endeavor" />}</Route>
        <Route path="/routes">{() => <Redirect to="/endeavor" />}</Route>
        <Route path="/create-route" component={CreateRoutePage} />
        <Route path="/route/:routeId/edit" component={RouteEditPage} />
        <Route path="/route/:routeId/share" component={RouteSharePage} />
        <Route path="/route/:routeId" component={RouteDetailPage} />
        {/* Phase B of unified entity maps: dedicated landing pages for conference + local event */}
        <Route path="/conference/:conferenceId" component={ConferencePage} />
        <Route path="/local-event/:eventId" component={LocalEventPage} />
        {/* IA Redesign: /the-scene replaces /local-vibes and /local-events */}
        <Route path="/the-scene">{() => <PageErrorBoundary><LocalEventsPage /></PageErrorBoundary>}</Route>
        <Route path="/local-vibes">{() => <Redirect to="/the-scene" />}</Route>
        <Route path="/local-events">{() => <Redirect to="/the-scene" />}</Route>
        {/* IA Redesign: Suit & Tie placeholder */}
        <Route path="/suit-and-tie" component={SuitAndTiePage} />
        <Route path="/create-entity" component={CreateEntityPage} />
        <Route path="/daily-guide" component={DailyGuidePage} />
        <Route path="/notifications" component={NotificationsPage} />

        <Route path="/points-miles" component={PointsMilesPage} />
        <Route path="/rewards" component={RewardsPage} />
        <Route path="/premium" component={Premium} />
        <Route path="/friends">{() => <PageErrorBoundary><FriendsPage /></PageErrorBoundary>}</Route>
        <Route path="/messages">{() => <PageErrorBoundary><MessagesPage /></PageErrorBoundary>}</Route>
        <Route path="/reviews" component={ReviewsPage} />
        <Route path="/community">{() => <PageErrorBoundary><CommunityBoard /></PageErrorBoundary>}</Route>
        <Route path="/favorite-board" component={FavoriteBoardPage} />
        <Route path="/favorites-leaderboard" component={FavoritesLeaderboardPage} />
        <Route path="/travel-calendar">{() => <PageErrorBoundary><TravelCalendarPage /></PageErrorBoundary>}</Route>
        <Route path="/referrals" component={ReferralsPage} />
        <Route path="/settings" component={SettingsPage} />
        <Route path="/transactions" component={TransactionsPage} />
        {DevDesignSystem && <Route path="/dev/design-system" component={DevDesignSystem} />}
        <Route path="/admin/moderation">{() => <AdminGuard><AdminModeration /></AdminGuard>}</Route>
        <Route path="/admin/privacy">{() => <AdminGuard><AdminPrivacy /></AdminGuard>}</Route>
        <Route path="/admin/strategy">{() => <AdminGuard><AdminStrategy /></AdminGuard>}</Route>
        <Route path="/admin/qr">{() => <AdminGuard><AdminQR /></AdminGuard>}</Route>
        <Route path="/profile/:userId">{() => <PageErrorBoundary><ProfilePage /></PageErrorBoundary>}</Route>
        {/* Canonical creature home — cold-hatch + management (council 2026-06-03) */}
        <Route path="/creature" component={CreaturePage} />
        <Route path="/photos" component={AllPhotosPage} />
        <Route component={NotFound} />
      </Switch>
      </Suspense>
      </ErrorBoundary>
    </AppLayout>
  );
}

function SSOCallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <AuthenticateWithRedirectCallback />
    </div>
  );
}

function Router() {
  return (
    <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
    <Switch>
      <Route path="/" component={Landing} />
      <Route path="/landingwithnewcrew" component={FunnelLanding} />
      <Route path="/privacy-policy" component={PrivacyPolicy} />
      <Route path="/terms-of-service" component={TermsOfService} />
      <Route path="/messaging-terms" component={MessagingTerms} />
      <Route path="/about" component={AboutUs} />
      <Route path="/help-center" component={HelpCenter} />
      <Route path="/contact" component={ContactUs} />
      <Route path="/cookie-policy" component={CookiePolicyPage} />
      <Route path="/accessibility" component={AccessibilityPage} />
      <Route path="/do-not-sell" component={DoNotSellPage} />
      <Route path="/ai-disclosure" component={AiDisclosurePage} />
      <Route path="/thank-you" component={ThankYouPage} />
      <Route path="/unsubscribe" component={MarketingUnsubscribePage} />
      <Route path="/s/:slug" component={SharedTripPage} />
      <Route path="/go/:slug" component={QrLandingPage} />
      <Route path="/festival/:festivalId" component={FestivalJoinPage} />
      <Route path="/sign-in">{() => <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><SignInPage /></Suspense>}</Route>
      <Route path="/sign-in/:rest*">{() => <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><SignInPage /></Suspense>}</Route>
      <Route path="/sign-up">{() => <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><SignUpPage /></Suspense>}</Route>
      <Route path="/sign-up/:rest*">{() => <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><SignUpPage /></Suspense>}</Route>
      <Route path="/sso-callback" component={SSOCallback} />
      <Route component={AppRoutes} />
    </Switch>
    </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
      <TooltipProvider>
        <SignupDialogProvider>
          <CreditWallProvider>
            <Toaster />
            <ConsentBanner />
            <Router />
            <SignupDialog />
          </CreditWallProvider>
        </SignupDialogProvider>
      </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
